/**
 * SubmitCommitButton — submits the current artifact draft for commit approval.
 * See specs/phase2/CR-D-artifact-editing-frontend-spec.md §3.10.
 *
 * Visible to PM / SA / admin only (UX gate; the backend re-enforces PM/SA on the endpoint).
 * The label + enabled state are derived from the artifact status, and may additionally be forced
 * disabled by the caller via `disabled` while another operation on the same artifact is running
 * (e.g. branded-doc regeneration). Clicking opens a small commit-message dialog (optional
 * override) before firing the submit request.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitCommitHorizontal, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  useSubmitCommitRequest,
  type ArtifactResponse,
  type ArtifactStatus,
} from "@/hooks/useArtifacts";

const SUBMIT_ROLES = ["pm", "sa", "admin", "leadership"];

interface StatusConfig {
  label: string;
  disabled: boolean;
}

/** Button label + enabled state per artifact status (spec §3.10 table). */
const STATUS_CONFIG: Record<ArtifactStatus, StatusConfig> = {
  generated: { label: "Submit for Commit", disabled: false },
  draft_edit: { label: "Submit for Commit", disabled: false },
  pending_commit: { label: "Commit Pending…", disabled: true },
  committed: { label: "Submit New Version", disabled: false },
  rejected: { label: "Resubmit for Commit", disabled: false },
};

export interface SubmitCommitButtonProps {
  artifact: ArtifactResponse;
  projectId: string;
  artifactType: string;
  userRole: string;
  onSubmitted: () => void;
  /**
   * Externally force the button into a disabled state while another operation on the same artifact
   * is in flight (e.g. a branded-doc regeneration). Cleared automatically by the caller when that
   * operation finishes, which re-enables the button.
   */
  disabled?: boolean;
  /** Label to show while `disabled` is externally forced. Falls back to the status label. */
  disabledLabel?: string;
}

export function SubmitCommitButton({
  artifact,
  projectId,
  artifactType,
  userRole,
  onSubmitted,
  disabled = false,
  disabledLabel,
}: SubmitCommitButtonProps): JSX.Element | null {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState("");
  const submit = useSubmitCommitRequest(projectId, artifactType);

  // Clear the message field each time the dialog opens.
  useEffect(() => {
    if (dialogOpen) setMessage("");
  }, [dialogOpen]);

  // Close the dialog if an external operation starts while it is open — submitting mid-regeneration
  // would commit stale content.
  useEffect(() => {
    if (disabled) setDialogOpen(false);
  }, [disabled]);

  // UX visibility gate — the endpoint independently enforces PM/SA.
  if (!SUBMIT_ROLES.includes(userRole)) return null;

  const config = STATUS_CONFIG[artifact.status];
  const isDisabled = config.disabled || disabled;
  const label = disabled && disabledLabel ? disabledLabel : config.label;

  const handleSubmit = async (): Promise<void> => {
    try {
      await submit.mutateAsync(message.trim() || undefined);
      toast.success("Submitted for approval");
      setDialogOpen(false);
      onSubmitted();
    } catch {
      // Shared axios interceptor surfaces the error toast; keep the dialog open.
    }
  };

  return (
    <>
      <Button
        onClick={() => setDialogOpen(true)}
        disabled={isDisabled}
        aria-label={label}
      >
        {isDisabled ? null : (
          <GitCommitHorizontal className="h-4 w-4" aria-hidden="true" />
        )}
        {label}
      </Button>

      <Dialog
        open={dialogOpen}
        onOpenChange={(next) => {
          if (!submit.isPending) setDialogOpen(next);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Submit for Commit</DialogTitle>
            <DialogDescription>
              Add an optional commit message. Leave blank to use an
              auto-generated message. This sends the draft for PM / Leadership
              approval.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-1">
            <Label htmlFor="commit-message">Commit message (optional)</Label>
            <Textarea
              id="commit-message"
              value={message}
              maxLength={2000}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="docs: update artifact…"
              className="min-h-[90px]"
              disabled={submit.isPending}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={submit.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submit.isPending}>
              {submit.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {submit.isPending ? "Submitting…" : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SubmitCommitButton;
